import React, { useMemo } from "react";
import { connect } from "react-redux";

import partition from "lodash-es/partition";

import { Role } from "@mapmycustomers/shared";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";

import SortOrder from "@app/scene/dashboard/components/Sidebar/enum/SortOrder";
import ViewType from "@app/scene/dashboard/enum/ViewType";
import PerformanceData from "@app/scene/dashboard/types/PerformanceData";
import { getCurrentUser } from "@app/store/iam";
import { isManager, isMember, isOwner, isReadOnly } from "@app/store/iam/util";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import Scope from "@app/types/dashboard/Scope";

import CollapsibleScopeItem from "./components/CollapsibleScopeItem";
import MetricValue from "./components/MetricValue";
import OrganizationLabel from "./components/OrganizationLabel";
import Owners from "./components/Owners";
import ScopeItem from "./components/ScopeItem";
import TeamLabel from "./components/TeamLabel";
import UserLabel from "./components/UserLabel";
import styles from "./ScopeList.module.scss";
import useSorters from "./utils/useSorters";

const doesTeamMatch = (team: Pick<Team, "name">, query: string = "") =>
  !query || team.name.toLowerCase().includes(query);

const doesUserMatch = (user: Pick<User, "fullName" | "username">, query: string = "") =>
  !query ||
  user.fullName?.toLowerCase().includes(query) ||
  user.username.toLowerCase().includes(query);

interface Props {
  activeScope: Scope;
  currentUser: User;
  performanceData: PerformanceData;
  performanceDataLoading?: boolean;
  query?: string;
  rankingMetric: IField;
  sort: SortOrder;
  teams: Team[];
  users: User[];
  view: ViewType;
}

const ScopeList: React.FC<Props> = ({
  activeScope,
  currentUser,
  performanceData,
  performanceDataLoading,
  query,
  rankingMetric,
  sort,
  teams,
  users,
  view,
}) => {
  const [owners, usersWithoutTeam] = useMemo(
    () =>
      partition(
        users.filter((user) => !user.teams.length && doesUserMatch(user, query)),
        isOwner
      ),
    [query, users]
  );

  const { teamSorter, userSorter } = useSorters(sort, rankingMetric, performanceData);

  const defaultScope: Scope = useMemo(() => {
    switch (currentUser.role.key) {
      case Role.READ_ONLY:
      case Role.MEMBER:
      case Role.MANAGER:
        return { teamId: currentUser.teams[0]?.id, userId: currentUser.id };
      default:
        return { teamId: undefined, userId: undefined };
    }
  }, [currentUser]);

  const showRankForNonTeamUsers = useMemo(
    () => usersWithoutTeam.some(({ id }) => !!performanceData[`noTeam_${id}`]?.value),
    [performanceData, usersWithoutTeam]
  );

  return (
    <div className={styles.container}>
      {isOwner(currentUser) && (
        <ScopeItem
          defaultScope={!defaultScope.teamId && !defaultScope.userId}
          key="entireOrganization"
          label={<OrganizationLabel />}
          selected={!activeScope.teamId && !activeScope.userId}
          suffix={
            <MetricValue
              loading={performanceDataLoading}
              metric={rankingMetric}
              performance={performanceData[`undefined_undefined`]}
            />
          }
          view={view}
        />
      )}
      <Owners
        activeScope={activeScope}
        currentUser={currentUser}
        owners={owners}
        performanceData={performanceData}
        performanceDataLoading={performanceDataLoading}
        rankingMetric={rankingMetric}
        sorter={userSorter}
        view={view}
      />
      {teamSorter(teams)
        .map((team) => {
          if (doesTeamMatch(team, query)) {
            return team; // return unchanged team
          }
          // else filter its users
          return { ...team, users: team.users.filter((user) => doesUserMatch(user, query)) };
        })
        .filter(({ users }) => users.length) // do not display empty teams
        .map((team) => {
          const showRank = team.users.some(
            ({ id }) => !!performanceData[`${team.id}_${id}`]?.value
          );

          return (
            <CollapsibleScopeItem
              clickable={!isMember(currentUser) && !isReadOnly(currentUser)}
              key={`team_${team.id}`}
              label={<TeamLabel team={team} />}
              selected={activeScope.teamId === team.id && !activeScope.userId}
              suffix={
                <MetricValue
                  loading={performanceDataLoading}
                  metric={rankingMetric}
                  performance={performanceData[`${team.id}_undefined`]}
                  userIds={team.users.map(({ id }) => id)}
                />
              }
              teamId={team.id}
              view={view}
            >
              {userSorter(team.users, team.id).map((user) => (
                <ScopeItem
                  clickable={
                    (!isMember(currentUser) && !isReadOnly(currentUser)) ||
                    currentUser.id === user.id
                  }
                  defaultScope={defaultScope.teamId === team.id && defaultScope.userId === user.id}
                  isManager={isManager(user)}
                  key={user.id}
                  label={
                    <UserLabel
                      performance={performanceData[`${team.id}_${user.id}`]}
                      showRank={showRank}
                      teamId={team.id}
                      user={user}
                    />
                  }
                  selected={activeScope.teamId === team.id && activeScope.userId === user.id}
                  suffix={
                    <MetricValue
                      loading={performanceDataLoading}
                      metric={rankingMetric}
                      performance={performanceData[`${team.id}_${user.id}`]}
                      userIds={[user.id]}
                    />
                  }
                  teamId={team.id}
                  userId={user.id}
                  view={view}
                />
              ))}
            </CollapsibleScopeItem>
          );
        })}
      {usersWithoutTeam.length > 0 && (
        <CollapsibleScopeItem
          clickable={false}
          key="noTeam"
          label={<TeamLabel team={undefined} />}
          suffix={
            <MetricValue
              loading={performanceDataLoading}
              metric={rankingMetric}
              performance={performanceData[`noTeam_undefined`]}
              userIds={usersWithoutTeam.map(({ id }) => id)}
            />
          }
          view={view}
        >
          {userSorter(usersWithoutTeam, "noTeam").map((user) => (
            <ScopeItem
              clickable={isOwner(currentUser) || currentUser.id === user.id}
              defaultScope={!defaultScope.teamId && defaultScope.userId === user.id}
              key={user.id}
              label={
                <UserLabel
                  performance={performanceData[`noTeam_${user.id}`]}
                  showRank={showRankForNonTeamUsers}
                  user={user}
                />
              }
              selected={!activeScope.teamId && activeScope.userId === user.id}
              suffix={
                <MetricValue
                  loading={performanceDataLoading}
                  metric={rankingMetric}
                  performance={performanceData[`noTeam_${user.id}`]}
                  userIds={[user.id]}
                />
              }
              userId={user.id}
              view={view}
            />
          ))}
        </CollapsibleScopeItem>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUser: getCurrentUser(state)!,
  teams: getTeams(state),
  users: getUsers(state),
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(ScopeList);
